import { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, TextField, CircularProgress,
    Alert, Stack, Switch, FormControlLabel, Select, MenuItem,
    FormControl, InputLabel, Chip, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, getDocs, addDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const PROMO_TYPES = ['free_months', 'discount_percent', 'discount_fixed'];
const TYPE_LABELS = { free_months: 'חודשים חינם', discount_percent: 'הנחה %', discount_fixed: 'הנחה ₪' };

const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
};

const emptyPromo = () => ({
    title: '',
    description: '',
    badgeText: '',
    type: 'discount_percent',
    value: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    targetPlans: ['basic', 'pro'],
    isActive: false,
});

const PromotionsManager = () => {
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [newPromo, setNewPromo] = useState(emptyPromo());

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDocs(collection(db, 'promotions'));
                const list = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(p => p.id !== 'placeholder');
                setPromos(list);
            } catch (e) {
                setError('שגיאה בטעינת מבצעים: ' + e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleToggleActive = async (promo) => {
        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'promotions', promo.id), { isActive: !promo.isActive }, { merge: true });
            setPromos(prev => prev.map(p => p.id === promo.id ? { ...p, isActive: !p.isActive } : p));
        } catch (e) {
            setError('שגיאה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (promo) => {
        if (!window.confirm(`מחק מבצע "${promo.title}"?`)) return;
        setSaving(true);
        try {
            await deleteDoc(doc(db, 'promotions', promo.id));
            setPromos(prev => prev.filter(p => p.id !== promo.id));
        } catch (e) {
            setError('שגיאה במחיקה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCreatePromo = async () => {
        if (!newPromo.title.trim() || !newPromo.validUntil) {
            setError('יש למלא שם מבצע ותאריך סיום.');
            return;
        }
        setSaving(true);
        setError('');
        setSuccessMsg('');
        try {
            const data = {
                ...newPromo,
                validFrom: new Date(newPromo.validFrom),
                validUntil: new Date(newPromo.validUntil),
                value: Number(newPromo.value),
                createdAt: new Date(),
            };
            const ref = await addDoc(collection(db, 'promotions'), data);
            setPromos(prev => [...prev, { id: ref.id, ...data }]);
            setSuccessMsg('מבצע נוצר בהצלחה!');
            setNewPromo(emptyPromo());
            setShowNew(false);
        } catch (e) {
            setError('שגיאה ביצירה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1e3a5f">
                    ניהול מבצעים
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { setShowNew(true); setSuccessMsg(''); setError(''); }}
                    sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, textTransform: 'none' }}
                >
                    מבצע חדש
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

            {/* New Promo Form */}
            {showNew && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '2px solid #1e3a5f' }}>
                    <Typography fontWeight={700} mb={2} fontSize="0.95rem">מבצע חדש</Typography>
                    <Stack gap={2}>
                        <Stack direction="row" gap={2}>
                            <TextField size="small" label="שם מבצע" value={newPromo.title}
                                onChange={e => setNewPromo(p => ({ ...p, title: e.target.value }))} sx={{ flex: 1 }} />
                            <TextField size="small" label="טקסט badge (לדוג׳ 🎉 מבצע)" value={newPromo.badgeText}
                                onChange={e => setNewPromo(p => ({ ...p, badgeText: e.target.value }))} sx={{ flex: 1 }} />
                        </Stack>
                        <TextField size="small" label="תיאור" value={newPromo.description}
                            onChange={e => setNewPromo(p => ({ ...p, description: e.target.value }))} />
                        <Stack direction="row" gap={2}>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>סוג מבצע</InputLabel>
                                <Select value={newPromo.type} label="סוג מבצע"
                                    onChange={e => setNewPromo(p => ({ ...p, type: e.target.value }))}>
                                    {PROMO_TYPES.map(t => <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField size="small" label="ערך" type="number" value={newPromo.value}
                                onChange={e => setNewPromo(p => ({ ...p, value: e.target.value }))} sx={{ flex: 1 }} />
                        </Stack>
                        <Stack direction="row" gap={2}>
                            <TextField size="small" label="תוקף מ" type="date" value={newPromo.validFrom}
                                onChange={e => setNewPromo(p => ({ ...p, validFrom: e.target.value }))}
                                InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                            <TextField size="small" label="תוקף עד" type="date" value={newPromo.validUntil}
                                onChange={e => setNewPromo(p => ({ ...p, validUntil: e.target.value }))}
                                InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                        </Stack>
                        <FormControlLabel control={
                            <Switch checked={newPromo.isActive} onChange={e => setNewPromo(p => ({ ...p, isActive: e.target.checked }))} />
                        } label={<Typography fontSize="0.85rem">פעיל מיד</Typography>} />
                        <Stack direction="row" gap={1.5}>
                            <Button variant="contained" onClick={handleCreatePromo} disabled={saving}
                                sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, textTransform: 'none' }}>
                                {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'צור מבצע'}
                            </Button>
                            <Button variant="outlined" onClick={() => setShowNew(false)}
                                sx={{ borderColor: '#e2e8f0', color: '#64748b', textTransform: 'none' }}>
                                ביטול
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Promotions list */}
            {promos.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
                    <Typography color="#94a3b8" fontSize="0.9rem">אין מבצעים. לחץ "מבצע חדש" להוספה.</Typography>
                </Paper>
            ) : (
                <Stack gap={2}>
                    {promos.map(promo => (
                        <Paper key={promo.id} sx={{ p: 2.5, borderRadius: '12px', border: promo.isActive ? '2px solid #10b981' : '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                        <Typography fontWeight={700} fontSize="0.95rem">{promo.title}</Typography>
                                        {promo.isActive && <Chip label="פעיל" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: '0.68rem', height: 20 }} />}
                                    </Box>
                                    {promo.badgeText && (
                                        <Typography fontSize="0.82rem" color="#64748b" mb={0.5}>{promo.badgeText}</Typography>
                                    )}
                                    {promo.description && (
                                        <Typography fontSize="0.8rem" color="#94a3b8">{promo.description}</Typography>
                                    )}
                                    <Typography fontSize="0.75rem" color="#cbd5e1" mt={0.75}>
                                        {TYPE_LABELS[promo.type]} | ערך: {promo.value} | תוקף: {formatDate(promo.validFrom)} — {formatDate(promo.validUntil)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                    <FormControlLabel
                                        control={<Switch size="small" checked={!!promo.isActive} onChange={() => handleToggleActive(promo)} disabled={saving} />}
                                        label=""
                                        sx={{ mr: 0 }}
                                    />
                                    <IconButton size="small" onClick={() => handleDelete(promo)} disabled={saving}
                                        sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default PromotionsManager;
